import {sortBy} from 'lodash';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {SearchOptions} from './models/search-options';
import {environment} from '../../environments/environment';
import {Material} from './models/material';
import {ActionQueue} from '@cicero/webui/lib/core/shared/action-queue';
import {MatSnackBar} from '@angular/material';
import {HttpClient} from '@angular/common/http';
import {AuthenticationService} from '../authentication/shared/authentication.service';
import {Params} from '@angular/router';

export interface CoverResult {
  thumbnail: string;
  detail: string;
}

interface SearchResponse {
  query: string;
  numberOfPages: number;
  numberOfResults: number;
  materials: any[];
}

@Injectable()
export class MaterialService {
  private foundQueue = new ActionQueue<Material>();
  private lostQueue = new ActionQueue<Material>();

  private searchOptions?: Observable<SearchOptions>;

  constructor(private http: HttpClient,
              private auth: AuthenticationService,
              private snackBar: MatSnackBar) {
    window.addEventListener('online', () => this.syncActions());
  }

  /**
   * Gets all possible search options for the agency
   * @returns {Observable<SearchOptions>}
   */
  getSearchOptions(): Observable<SearchOptions> {
    if (this.searchOptions) {
      return this.searchOptions;
    }

    // Clear cache when it expires
    setTimeout(() => this.searchOptions = undefined, environment.searchOptionsCacheTimeMs);

    // Cache the HTTP response
    return this.searchOptions = this.http.get(
      `${environment.apiUrl}/material/searchOptions`,
      {headers: this.auth.getAuthHeaders()}
    )
      .map(options => this.parseSearchOptions(options))
      .publishReplay(1)
      .refCount();
  }

  search(query: Params, page: number): Observable<{
    query: string, numberOfResults: number, numberOfPages: number,
    materials: Material[]
  }> {
    return this.http.get<SearchResponse>(
      `${environment.apiUrl}/material/search`,
      {headers: this.auth.getAuthHeaders(), params: Object.assign({}, query, {page: page})}
    )
      .do(result => result.materials = this.parseMaterials(result.materials));
  }

  undo(material: Material): Observable<string> {
    if (!material.canUndo) {
      return Observable.throw('Undoing the latest action on this material is not allowed');
    }

    let request: Observable<any> = Observable.empty();

    if (material.localState === 'found') {
      if (!window.navigator.onLine) {
        this.foundQueue.undo(material.materialId, material);
      } else {
        request = this.sendUndoFoundRequest(material);
      }
    } else if (material.localState === 'lost') {
      if (!window.navigator.onLine) {
        this.lostQueue.undo(material.materialId, material);
      } else {
        request = this.sendUndoLostRequest(material);
      }
    }

    material.localState = undefined;
    material.canUndo = false;

    return request;
  }

  markFound(material: Material) {
    material.localState = 'found';
    material.canUndo = true;

    if (!window.navigator.onLine) {
      this.foundQueue.mark(material.materialId, material);
      return Observable.empty();
    } else {
      return this.sendMarkFoundRequest(material);
    }
  }

  markLost(material: Material) {
    material.localState = 'lost';
    material.canUndo = true;

    if (!window.navigator.onLine) {
      this.lostQueue.mark(material.materialId, material);
      return Observable.empty();
    } else {
      return this.sendMarkLostRequest(material);
    }
  }

  getCovers(faustNumbers: string[]): Observable<{ [faustNumber: string]: CoverResult }> {
    return this.http.get<{ [faustNumber: string]: CoverResult }>(
      `${environment.apiUrl}/cover/${faustNumbers.join(',')}`,
      {headers: this.auth.getAuthHeaders()}
    );
  }

  private syncActions() {
    this.foundQueue.getActions().forEach(action => {
      if (action.isUndo) {
        this.sendUndoFoundRequest(action.data).subscribe();
      } else {
        this.sendMarkFoundRequest(action.data).subscribe();
      }
    });

    this.lostQueue.getActions().forEach(action => {
      if (action.isUndo) {
        this.sendUndoLostRequest(action.data).subscribe();
      } else {
        this.sendMarkLostRequest(action.data).subscribe();
      }
    });

    this.foundQueue.clear();
    this.lostQueue.clear();
  }

  private sendUndoFoundRequest(material: Material): Observable<string> {
    return this.http.put(
      `${environment.apiUrl}/material/${material.materialId}/found/undo`,
      {
        previousThemeId: material.themeId,
        previousMaterialState: material.state
      },
      {headers: this.auth.getAuthHeaders(), responseType: 'text'}
    );
  }

  private sendUndoLostRequest(material: Material): Observable<string> {
    return this.http.put(
      `${environment.apiUrl}/material/${material.materialId}/lost/undo`,
      {
        previousThemeId: material.themeId,
        previousMaterialState: material.state
      },
      {headers: this.auth.getAuthHeaders(), responseType: 'text'}
    );
  }

  private sendMarkFoundRequest(material: Material): Observable<{ canFulfillReservation: boolean }> {
    return this.http.put<{ canFulfillReservation: boolean }>(
      `${environment.apiUrl}/material/${material.materialId}/found`,
      null,
      {headers: this.auth.getAuthHeaders()}
    )
      .do(data => {
        if (!data.canFulfillReservation) {
          return;
        }

        this.snackBar.open(
          'Materialet kan opfylde en reservation',
          undefined,
          {duration: environment.errorMessageDurationMs}
        );
      });
  }

  private sendMarkLostRequest(material: Material): Observable<string> {
    return this.http.put(
      `${environment.apiUrl}/material/${material.materialId}/lost`,
      null,
      {headers: this.auth.getAuthHeaders(), responseType: 'text'}
    );
  }

  private parseSearchOptions(options: any): SearchOptions {
    const stateNames: any = {
      AVAILABLE: 'Hjemme',
      LENDOUT: 'Udlånt',
      LENDOUT_WITH_COMPENSATION: 'Udlånt med erstatning',
      READY_FOR_PICKUP: 'Klar til afhentning',
      ORDERED: 'Bestilt',
      LOST: 'Bortkommet',
      SENT_FROM_SUPPLIER: 'Undervejs',
      IN_TRANSIT: 'I transit',
      DAMAGED: 'Beskadiget',
      DISCARDED: 'Kasseret',
      NOT_DELIVERED: 'Ikke leveret',
      CLOSED_ILL: 'Afsluttet fjernlån'
    };

    options.branches = sortBy(options.branches, 'shortName');

    options.placements.departments = sortBy(options.placements.departments, 'shortName');
    options.placements.locations = sortBy(options.placements.locations, 'shortName');
    options.placements.sublocations = sortBy(options.placements.sublocations, 'shortName');

    options.themes = sortBy(options.themes, 'name');

    options.states = options.states.map((id: string) => ({id: id, name: stateNames[id]}));
    options.states = sortBy(options.states, 'name');

    return options;
  }

  private parseMaterials(materials: any[]): Material[] {
    materials.forEach(material => {
      if (material.title) {
        const indexOfSortingChar = material.title.indexOf('¤');

        if (indexOfSortingChar > -1) {
          material.sortableTitle = material.title.slice(indexOfSortingChar + 1);
          material.title = material.title.replace('¤', '');
        } else {
          material.sortableTitle = material.title;
        }
      }
    });

    return materials;
  }
}



// WEBPACK FOOTER //
// ./src/app/material/material.service.ts